/**
 * Marketplace resolution utility for the Dashboard.
 *
 * Replaces the hardcoded `common.py` get_marketplace_list() logic with a
 * dynamic approach that uses marketplace and region data fetched from the
 * FINN RDS database via GraphQL (getMarketplaces / getRegions).
 *
 * The Region type from GraphQL has `marketplaces: AWSJSON` — a JSON string
 * that must be parsed before use.
 */

/** Matches the GraphQL Marketplace type. */
export interface Marketplace {
  marketplace_id: number;
  country_code: string;
  display_name: string;
}

/**
 * Matches the GraphQL Region type.
 * `marketplaces` is AWSJSON (a JSON-encoded string) from AppSync.
 */
export interface Region {
  region_code: string;
  display_name: string;
  marketplaces: string; // AWSJSON — must be parsed
}

/** Parsed marketplace entry inside a region's AWSJSON blob. */
interface RegionMarketplace {
  marketplace_id: number;
  country_code: string;
  display_name: string;
}

/**
 * Resolve a geo code to a list of marketplace IDs and a display name.
 *
 * - If `geo` matches a region code → return that region's marketplace IDs
 *   and the region's display name.
 * - If `geo` is a marketplace ID (numeric string) → return [that ID] and
 *   the marketplace's country code.
 * - Otherwise throw an error for invalid geo values.
 *
 * @param geo          - Region code (e.g. 'ww', 'na') or marketplace ID string (e.g. '1')
 * @param regions      - Region list from getRegions GraphQL query
 * @param marketplaces - Marketplace list from getMarketplaces GraphQL query
 * @returns Object with marketplaceIds array and geoGroupName string
 */
export function getMarketplaceList(
  geo: string,
  regions: Region[],
  marketplaces: Marketplace[]
): { marketplaceIds: number[]; geoGroupName: string } {
  // Check if geo matches a region code
  const region = regions.find((r) => r.region_code === geo);
  if (region) {
    // AWSJSON can be double-encoded: parse until we get an array
    let parsed: any = region.marketplaces;
    while (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        parsed = [];
        break;
      }
    }
    const marketplaceList: RegionMarketplace[] = Array.isArray(parsed)
      ? parsed
      : [];

    return {
      marketplaceIds: marketplaceList.map((m) => m.marketplace_id),
      geoGroupName: region.display_name,
    };
  }

  // Check if geo is a valid marketplace ID
  const geoId = parseInt(geo, 10);
  if (!isNaN(geoId)) {
    const marketplace = marketplaces.find((m) => m.marketplace_id === geoId);
    if (marketplace) {
      return {
        marketplaceIds: [geoId],
        geoGroupName: marketplace.country_code,
      };
    }
  }

  throw new Error(`Invalid geo value: ${geo}`);
}
